import React, { useState, useEffect, useRef } from 'react';
import { FiAward, FiPlus, FiMinus, FiCheckCircle, FiCircle, FiSave, FiRefreshCw, FiBarChart2 } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const WinTheDay = () => {
  const [activities, setActivities] = useState({
    newProspects: 0,
    verifiedContacts: 0,
    coldEmails: 0,
    coldCalls: 0,
    opportunitiesCreated: 0,
    meetingsSet: 0,
    selfDevelopment: 0
  });
  
  const [savedDate, setSavedDate] = useState('');
  const [totalPoints, setTotalPoints] = useState(0);
  const [isWinner, setIsWinner] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  const [history, setHistory] = useState([]);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Goal is 8 points
  const GOAL_POINTS = 8;

  // Categories and their descriptions
  const categories = [
    {
      id: 'newProspects',
      label: 'New Prospects',
      description: '1 point per 8 new prospects added',
      increment: 8,
      color: 'blue'
    },
    {
      id: 'verifiedContacts',
      label: 'Verified Contacts',
      description: '1 point per contact verification',
      increment: 1,
      color: 'green'
    },
    {
      id: 'coldEmails',
      label: 'Cold Emails',
      description: '1 point per 8 cold emails sent',
      increment: 8,
      color: 'purple'
    },
    {
      id: 'coldCalls',
      label: 'Cold Calls',
      description: '1 point per 4 cold calls made',
      increment: 4,
      color: 'orange'
    },
    {
      id: 'opportunitiesCreated',
      label: 'Opportunities Created',
      description: '1 point per new opportunity',
      increment: 1,
      color: 'indigo'
    },
    {
      id: 'meetingsSet',
      label: 'Meetings/Demos Set',
      description: '1 point per meeting scheduled',
      increment: 1,
      color: 'pink'
    },
    {
      id: 'selfDevelopment',
      label: 'Self Development',
      description: '1 point for daily learning/improvement',
      increment: 1,
      color: 'cyan'
    }
  ];

  // Load saved data on component mount
  useEffect(() => {
    const savedActivities = localStorage.getItem('winTheDayActivities');
    const savedDateString = localStorage.getItem('winTheDayDate');
    const savedHistory = localStorage.getItem('winTheDayHistory');
    
    // Check if we have saved data from today
    const today = new Date().toLocaleDateString();
    
    if (savedActivities && savedDateString === today) {
      setActivities(JSON.parse(savedActivities));
      setSavedDate(savedDateString);
    } else {
      // Reset for a new day
      resetActivities();
      setSavedDate(today);
    }
    
    if (savedHistory) {
      setHistory(JSON.parse(savedHistory));
    }
  }, []);

  // Calculate total points whenever activities change
  useEffect(() => {
    const points = calculatePoints();
    setTotalPoints(points);
    setIsWinner(points >= GOAL_POINTS);
    
    // Save to localStorage
    const today = new Date().toLocaleDateString();
    localStorage.setItem('winTheDayActivities', JSON.stringify(activities));
    localStorage.setItem('winTheDayDate', today);
  }, [activities]);

  // Calculate points based on activities
  const calculatePoints = () => {
    let points = 0;
    points += Math.floor(activities.newProspects / 8);
    points += activities.verifiedContacts;
    points += Math.floor(activities.coldEmails / 8);
    points += Math.floor(activities.coldCalls / 4);
    points += activities.opportunitiesCreated;
    points += activities.meetingsSet;
    points += activities.selfDevelopment;
    return points;
  };

  // Increment an activity
  const incrementActivity = (id, increment = 1) => {
    setActivities(prev => ({
      ...prev,
      [id]: prev[id] + increment
    }));
  };

  // Decrement an activity (not below 0)
  const decrementActivity = (id, decrement = 1) => {
    setActivities(prev => ({
      ...prev,
      [id]: Math.max(0, prev[id] - decrement)
    }));
  };

  // Reset all activities to 0
  const resetActivities = () => {
    setActivities({
      newProspects: 0,
      verifiedContacts: 0,
      coldEmails: 0,
      coldCalls: 0,
      opportunitiesCreated: 0,
      meetingsSet: 0,
      selfDevelopment: 0
    });
    setSaveSuccess(false);
  };

  // Save today's results to history
  const saveToHistory = () => {
    const today = new Date();
    const historyEntry = {
      date: today.toLocaleDateString(),
      points: totalPoints,
      isWinner: isWinner,
      activities: { ...activities }
    };
    
    const updatedHistory = [historyEntry, ...history].slice(0, 30); // Keep only last 30 days
    setHistory(updatedHistory);
    localStorage.setItem('winTheDayHistory', JSON.stringify(updatedHistory));
    
    // Show success message
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  // Get points for a specific category
  const getCategoryPoints = (category) => {
    const { id, increment } = category;
    if (increment === 1) return activities[id];
    return Math.floor(activities[id] / increment);
  };

  // Get background color class based on winning status
  const getBackgroundColor = () => {
    if (isWinner) return 'bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 border-green-200 dark:border-green-800';
    return 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700';
  };

  // Get specific color classes for a category
  const getCategoryColorClasses = (color) => {
    const colorMap = {
      'blue': {
        bg: 'bg-blue-100 dark:bg-blue-900/30',
        hover: 'hover:bg-blue-200 dark:hover:bg-blue-800/30',
        text: 'text-blue-600 dark:text-blue-400'
      },
      'green': {
        bg: 'bg-green-100 dark:bg-green-900/30',
        hover: 'hover:bg-green-200 dark:hover:bg-green-800/30',
        text: 'text-green-600 dark:text-green-400'
      },
      'purple': {
        bg: 'bg-purple-100 dark:bg-purple-900/30',
        hover: 'hover:bg-purple-200 dark:hover:bg-purple-800/30',
        text: 'text-purple-600 dark:text-purple-400'
      },
      'orange': {
        bg: 'bg-orange-100 dark:bg-orange-900/30',
        hover: 'hover:bg-orange-200 dark:hover:bg-orange-800/30',
        text: 'text-orange-600 dark:text-orange-400'
      },
      'indigo': {
        bg: 'bg-indigo-100 dark:bg-indigo-900/30',
        hover: 'hover:bg-indigo-200 dark:hover:bg-indigo-800/30',
        text: 'text-indigo-600 dark:text-indigo-400'
      },
      'pink': {
        bg: 'bg-pink-100 dark:bg-pink-900/30',
        hover: 'hover:bg-pink-200 dark:hover:bg-pink-800/30',
        text: 'text-pink-600 dark:text-pink-400'
      },
      'cyan': {
        bg: 'bg-cyan-100 dark:bg-cyan-900/30',
        hover: 'hover:bg-cyan-200 dark:hover:bg-cyan-800/30',
        text: 'text-cyan-600 dark:text-cyan-400'
      }
    };
    
    return colorMap[color] || colorMap.blue;
  };

  // Calculate streak based on history
  const calculateStreak = () => {
    if (history.length === 0) return 0;
    
    let streak = 0;
    const today = new Date().toLocaleDateString();
    
    // Start from most recent history entry
    for (let i = 0; i < history.length; i++) {
      const entry = history[i];
      
      // Skip today's entry if it exists
      if (i === 0 && entry.date === today) continue;
      
      // Check if the entry is a win
      if (entry.isWinner) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  };

  // Trend graph data for the last 7 days
  const getTrendData = () => {
    const trendData = [];
    const today = new Date();
    
    // Create array for the last 7 days
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateString = date.toLocaleDateString();
      
      // Find history entry for this date
      const entry = history.find(h => h.date === dateString);
      
      trendData.push({
        date: dateString,
        points: entry ? entry.points : 0,
        isWin: entry ? entry.isWinner : false,
        label: i === 0 ? 'Today' : i === 1 ? 'Yesterday' : date.toLocaleDateString('en-US', { weekday: 'short' })
      });
    }
    
    return trendData;
  };
  
  const trendData = getTrendData();
  const streak = calculateStreak();

  return (
    <div className={`rounded-lg shadow-sm border p-6 transition-all duration-500 ${getBackgroundColor()}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <SafeIcon 
            icon={FiAward} 
            className={`w-6 h-6 ${isWinner ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`} 
          />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Did You Win The Day?</h3>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => setShowGraph(!showGraph)} 
            className={`text-sm px-2 py-1 rounded ${showGraph ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'text-blue-600 dark:text-blue-400 hover:underline'}`}
          >
            <div className="flex items-center">
              <SafeIcon icon={FiBarChart2} className="w-4 h-4 mr-1" />
              {showGraph ? 'Hide Graph' : 'Show Graph'}
            </div>
          </button>
          <button 
            onClick={() => setShowHistory(!showHistory)} 
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            {showHistory ? 'Hide History' : 'Show History'}
          </button>
        </div>
      </div>
      
      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <div className="text-sm text-gray-600 dark:text-gray-400">Daily Goal: {totalPoints}/{GOAL_POINTS} points</div>
          <div className={`text-sm font-medium ${isWinner ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
            {isWinner ? '🏆 Goal achieved!' : `${GOAL_POINTS - totalPoints} more to win`}
          </div>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
          <div 
            className={`h-2.5 rounded-full transition-all duration-500 ${isWinner ? 'bg-green-500 dark:bg-green-400' : 'bg-blue-500 dark:bg-blue-400'}`} 
            style={{ width: `${Math.min(100, (totalPoints / GOAL_POINTS) * 100)}%` }}
          ></div>
        </div>
      </div>
      
      {/* Streak indicator */}
      {streak > 0 && (
        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-amber-600 dark:text-amber-400 text-lg mr-2">🔥</span>
              <span className="font-medium text-amber-800 dark:text-amber-300">Win streak: {streak} day{streak !== 1 ? 's' : ''}</span>
            </div>
            <div className="text-xs text-amber-600 dark:text-amber-400">Keep it going!</div>
          </div>
        </div>
      )}

      {/* Trend Graph */}
      {showGraph && (
        <div className="mb-6 p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">7-Day Trend</h4>
          <div className="relative h-40">
            {/* Goal line */}
            <div 
              className="absolute w-full border-t border-dashed border-gray-300 dark:border-gray-600 flex justify-end"
              style={{ top: `${100 - ((GOAL_POINTS / 10) * 100)}%` }}
            >
              <span className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-1 -mt-2">Goal ({GOAL_POINTS})</span>
            </div>
            
            {/* Bars */}
            <div className="flex items-end justify-between h-32 gap-1">
              {trendData.map((day, index) => (
                <div key={index} className="flex flex-col items-center flex-1">
                  <div className="w-full relative">
                    <div 
                      className={`w-full transition-all duration-500 ${day.isWin ? 'bg-green-500 dark:bg-green-400' : day.points > 0 ? 'bg-blue-500 dark:bg-blue-400' : 'bg-gray-300 dark:bg-gray-600'}`}
                      style={{ 
                        height: `${Math.max(4, (day.points / 10) * 100)}%`,
                        minHeight: day.points > 0 ? '4px' : '0'
                      }}
                    ></div>
                    {day.points > 0 && (
                      <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 text-xs font-medium text-gray-700 dark:text-gray-300">
                        {day.points}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 whitespace-nowrap overflow-hidden text-ellipsis max-w-[40px] text-center">
                    {day.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Activity Tracking */}
      <div className="space-y-4">
        {categories.map(category => {
          const points = getCategoryPoints(category);
          const hasPoints = points > 0;
          const colorClasses = getCategoryColorClasses(category.color);
          
          return (
            <div key={category.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <SafeIcon 
                    icon={hasPoints ? FiCheckCircle : FiCircle} 
                    className={`w-5 h-5 mr-2 ${hasPoints ? colorClasses.text : 'text-gray-400 dark:text-gray-500'}`} 
                  />
                  <span className="font-medium text-gray-800 dark:text-gray-200">{category.label}</span>
                </div>
                <div className="flex items-center space-x-1 text-sm">
                  <span className={`font-medium ${hasPoints ? colorClasses.text : 'text-gray-500 dark:text-gray-400'}`}>
                    {points} point{points !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {category.description}
                  <div className="mt-1">
                    Current: {activities[category.id]} {category.id === 'newProspects' ? 'prospects' : 
                              category.id === 'coldEmails' ? 'emails' : 
                              category.id === 'coldCalls' ? 'calls' : ''}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => decrementActivity(category.id, 1)}
                    className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center justify-center transition-colors"
                    aria-label={`Decrease ${category.label}`}
                    disabled={activities[category.id] === 0}
                  >
                    <SafeIcon icon={FiMinus} className={`w-4 h-4 ${activities[category.id] === 0 ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-300'}`} />
                  </button>
                  <button
                    onClick={() => incrementActivity(category.id, 1)}
                    className={`w-7 h-7 rounded-full ${colorClasses.bg} ${colorClasses.hover} flex items-center justify-center transition-colors`}
                    aria-label={`Increase ${category.label}`}
                  >
                    <SafeIcon icon={FiPlus} className={`w-4 h-4 ${colorClasses.text}`} />
                  </button>
                </div>
              </div>
              
              {/* Progress bar for this category */}
              {category.increment > 1 && activities[category.id] > 0 && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                    <div 
                      className={`h-1.5 rounded-full transition-all duration-300`}
                      style={{ 
                        width: `${Math.min(100, ((activities[category.id] % category.increment) / category.increment) * 100)}%`,
                        backgroundColor: hasPoints ? colorClasses.text.replace('text', 'bg') : 'bg-gray-400'
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-gray-400 dark:text-gray-500">
                    <span>0</span>
                    <span>{category.increment}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Action Buttons */}
      <div className="mt-6 flex justify-between">
        <button
          onClick={resetActivities}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
          aria-label="Reset all activities"
        >
          <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
          Reset
        </button>
        <button
          onClick={saveToHistory}
          className={`px-4 py-2 rounded-lg text-white transition-colors flex items-center ${
            isWinner 
              ? 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600' 
              : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600'
          }`}
          aria-label="Save progress"
        >
          <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
          {isWinner ? 'Save Win! 🎉' : 'Save Progress'}
        </button>
      </div>
      
      {/* Success Message */}
      {saveSuccess && (
        <div className="mt-4 p-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm rounded-lg border border-green-200 dark:border-green-800/50 text-center">
          Progress saved successfully!
        </div>
      )}
      
      {/* History Section */}
      {showHistory && (
        <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
          <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-3">Recent History</h4>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {history.length > 0 ? (
              history.map((entry, index) => (
                <div 
                  key={index} 
                  className={`p-3 rounded-lg border ${
                    entry.isWinner 
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50' 
                      : 'bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div className="font-medium text-sm text-gray-800 dark:text-gray-200">{entry.date}</div>
                    <div className={`text-sm font-medium ${
                      entry.isWinner 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {entry.points}/{GOAL_POINTS} points {entry.isWinner && '🏆'}
                    </div>
                  </div>
                  
                  {/* Activity breakdown */}
                  <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                    {categories.map(cat => {
                      const activityCount = entry.activities[cat.id];
                      const catPoints = cat.increment === 1 
                        ? activityCount 
                        : Math.floor(activityCount / cat.increment);
                      
                      if (catPoints > 0) {
                        return (
                          <div key={cat.id} className="flex items-center">
                            <div className={`w-2 h-2 rounded-full ${getCategoryColorClasses(cat.color).bg} mr-1`}></div>
                            <span className="text-gray-600 dark:text-gray-400 truncate">
                              {cat.label}: {catPoints}pt
                            </span>
                          </div>
                        );
                      }
                      return null;
                    }).filter(Boolean)}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                No history yet. Save today's progress to start tracking!
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WinTheDay;